// src/components/common/SettingsContent.js
import React, { useState } from 'react';
import { Type, Bell, Lock, Mail, Check, AlertCircle, Smartphone, Monitor, Clock, Shield, Key, Eye, EyeOff, LogOut } from 'lucide-react';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/auth.service';
import { api } from '../../services/api';

const SettingsContent = () => {
    const { user } = useAuth();
    const {
        fontSize,
        updateFontSize,
        notificationSettings,
        updateNotificationSetting,
        emailPreferences,
        updateEmailPreferences,
        securitySettings,
        updateSecuritySettings,
        saveSettings
    } = useUserSettings();

    const [activeTab, setActiveTab] = useState('font');
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Password change state
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [forceLogoutLoading, setForceLogoutLoading] = useState(false);

    // Email state
    const [emailData, setEmailData] = useState({
        primaryEmail: emailPreferences.primaryEmail || user?.email || '',
        secondaryEmail: emailPreferences.secondaryEmail || '',
        emailSignature: emailPreferences.emailSignature || '',
        newsletter: emailPreferences.newsletter !== undefined ? emailPreferences.newsletter : true
    });

    const tabs = [
        { id: 'font', label: 'Font Settings', icon: Type },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Password & Security', icon: Lock },
        { id: 'email', label: 'Email Preferences', icon: Mail }
    ];

    const handleSave = async () => {
        try {
            await saveSettings({
                fontSize,
                notificationSettings,
                emailPreferences: emailData,
                securitySettings
            });
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (error) {
            setShowError(true);
            setErrorMessage('Failed to save settings. Please try again.');
            setTimeout(() => setShowError(false), 3000);
        }
    };

    const handlePasswordChange = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setErrorMessage('New passwords do not match');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }

        if (passwordData.newPassword.length < 8) {
            setErrorMessage('Password must be at least 8 characters');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }

        try {
            await authService.changePassword(
                passwordData.currentPassword,
                passwordData.newPassword
            );
            setShowSuccess(true);
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

            // Auto-logout after 2 seconds
            setTimeout(() => {
                authService.logout();
            }, 2000);
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Failed to change password. Please check your current password.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        }
    };

    const handleForceLogoutAll = async () => {
        if (!window.confirm('This will log out ALL users immediately. They will need to login again. Are you sure?')) {
            return;
        }

        setForceLogoutLoading(true);
        try {
            const response = await api.post('/auth/force-logout-all');
            setShowSuccess(true);
            alert(`Success! ${response.data.message}\n\nAll users (including you) will be logged out within 5 minutes.`);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (error) {
            setErrorMessage(error.response?.data?.error || 'Failed to force logout all users.');
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        } finally {
            setForceLogoutLoading(false);
        }
    };

    const renderFontSettings = () => (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Font Size
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Choose your preferred text size across the application
                </p>

                <div className="grid grid-cols-3 gap-4">
                    {['small', 'medium', 'large'].map((size) => (
                        <button
                            key={size}
                            onClick={() => updateFontSize(size)}
                            className={`p-6 border-2 rounded-lg text-center transition-all ${
                                fontSize === size
                                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                        >
                            <div className={`font-semibold mb-2 ${
                                size === 'small' ? 'text-sm' : size === 'large' ? 'text-xl' : 'text-base'
                            }`}>
                                {size === 'small' ? 'Aa' : size === 'medium' ? 'Aa' : 'Aa'}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                {size}
                            </div>
                            {fontSize === size && (
                                <div className="mt-2">
                                    <Check className="w-5 h-5 text-blue-600 mx-auto" />
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Preview</h4>
                <p className="text-gray-700 dark:text-gray-300">
                    This is how your text will appear with the selected font size. The quick brown fox jumps over the lazy dog.
                </p>
            </div>
        </div>
    );

    const renderNotificationSettings = () => (
        <div className="space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            Email Notifications
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Receive notifications via email
                        </p>
                    </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={notificationSettings.emailEnabled !== false}
                        onChange={(e) => updateNotificationSetting('emailEnabled', null, e.target.checked)}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
            </div>

            {/* In-App Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            In-App Notifications
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Receive notifications within the application
                        </p>
                    </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={notificationSettings.inAppEnabled !== false}
                        onChange={(e) => updateNotificationSetting('inAppEnabled', null, e.target.checked)}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
            </div>
        </div>
    );

    const renderSecuritySettings = () => (
        <div className="space-y-6">
            {/* Change Password */}
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Change Password
                    </h3>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                value={passwordData.currentPassword}
                                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                value={passwordData.newPassword}
                                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                placeholder="Enter new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Must be at least 8 characters
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={passwordData.confirmPassword}
                                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                placeholder="Confirm new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={handlePasswordChange}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Update Password
                    </button>
                </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="border-t dark:border-gray-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Two-Factor Authentication
                    </h3>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                        <p className="font-medium text-gray-700 dark:text-gray-300">Enable 2FA</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Add an extra layer of security to your account
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={securitySettings.twoFactorEnabled}
                            onChange={(e) => updateSecuritySettings({ twoFactorEnabled: e.target.checked })}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            {/* Active Sessions */}
            <div className="border-t dark:border-gray-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Monitor className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Active Sessions
                    </h3>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-3">
                            <Monitor className="w-5 h-5 text-green-600" />
                            <div>
                                <p className="font-medium text-gray-700 dark:text-gray-300">Current Session</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                </p>
                            </div>
                        </div>
                        <span className="text-xs text-green-600 font-semibold">ACTIVE</span>
                    </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    You'll be able to see all your active sessions and logout from specific devices
                </p>
            </div>

            {/* Login History */}
            <div className="border-t dark:border-gray-700 pt-6">
                <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Recent Login History
                    </h3>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    Login history will be displayed here
                </div>
            </div>

            {/* Admin Only: Force Logout All Users */}
            {user?.isAdmin && (
                <div className="border-t dark:border-gray-700 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                        <LogOut className="w-5 h-5 text-red-600" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Force Logout All Users
                        </h3>
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">Admin Only</span>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                            Use this after deploying new code to force all users to reload the application
                            and get the latest version. All active sessions will be invalidated within 5 minutes.
                        </p>
                        <button
                            onClick={handleForceLogoutAll}
                            disabled={forceLogoutLoading}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {forceLogoutLoading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <LogOut className="w-4 h-4" />
                                    Force Logout All Users
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderEmailPreferences = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Primary Email
                </label>
                <input
                    type="email"
                    value={emailData.primaryEmail}
                    onChange={(e) => setEmailData({...emailData, primaryEmail: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="your.email@company.com"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Secondary Email (Optional)
                </label>
                <input
                    type="email"
                    value={emailData.secondaryEmail}
                    onChange={(e) => setEmailData({...emailData, secondaryEmail: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="secondary@email.com"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Signature
                </label>
                <textarea
                    value={emailData.emailSignature}
                    onChange={(e) => setEmailData({...emailData, emailSignature: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Your email signature for system-generated emails"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This signature will be appended to system emails sent on your behalf
                </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                    <p className="font-medium text-gray-700 dark:text-gray-300">Newsletter Subscription</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Receive updates about new features and company news
                    </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={emailData.newsletter}
                        onChange={(e) => setEmailData({...emailData, newsletter: e.target.checked})}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header with Save Button */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your account preferences</p>
                </div>
                <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Check className="w-4 h-4" />
                    Save All Changes
                </button>
            </div>

            {/* Success/Error Messages */}
            {showSuccess && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <p className="text-green-800 dark:text-green-200">Settings saved successfully!</p>
                </div>
            )}

            {showError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-red-800 dark:text-red-200">{errorMessage}</p>
                </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-12 gap-6">
                {/* Sidebar Tabs */}
                <div className="col-span-3">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-2">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                                        activeTab === tab.id
                                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="font-medium">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Content Area */}
                <div className="col-span-9">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
                        {activeTab === 'font' && renderFontSettings()}
                        {activeTab === 'notifications' && renderNotificationSettings()}
                        {activeTab === 'security' && renderSecuritySettings()}
                        {activeTab === 'email' && renderEmailPreferences()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsContent;
